import { LicenseBalance, isLicensingNotConfigured } from '../api/licensing';
import type { MintReadiness } from '../api/waves';
import { deviceCodeBlockState } from './deviceCodeBlocking';

// FD-611 — the activation dialog's readiness checks, as data.
//
// One derivation feeds BOTH the compact summary ("N checks passed · M items need
// attention") and the Advanced details checklist, so the two can never count the
// same facts two different ways. Every number the summary shows is COUNTED from
// these rows; nothing here is a constant.
//
// FOUR statuses, and the split is the honest-UI rule (FD-ADR-043):
//
//   `pass`       — the check ran and found nothing wrong.
//   `attention`  — the check ran and found something an operator should look at.
//   `unverified` — the check could not decide (FD-609's `null`, a failed read, an
//                  account the probe never reached). NEVER folded into `pass`: a
//                  question nobody could answer is not a green light.
//   `note`       — true and worth saying, but neither a pass nor a problem: licensing
//                  switched off, or a device-code restriction while the operator is
//                  using a different sign-in path.
//
// ADVISORY, all of it. Nothing derived here gates the primary button —
// `activate-wave` refuses on its own terms (FD-296), and a console-only gate only
// runs where someone happens to be standing.

export type SignInMethod = 'browser' | 'device-code' | 'paste';

export type ActivationCheckStatus = 'pass' | 'attention' | 'unverified' | 'note';

export type ActivationCheckId =
  | 'account'
  | 'account-checks'
  | 'exists'
  | 'role'
  | 'conditional-access'
  | 'device-code'
  | 'licence';

export interface ActivationCheck {
  id: ActivationCheckId;
  label: string;
  status: ActivationCheckStatus;
  /** The verdict in words — the same wording the minting-account panel has always used. */
  value: string;
  /** A short qualifier shown beside the value (e.g. "affects device code only"). */
  hint?: string;
}

export interface QueryState<T> {
  data: T | null | undefined;
  isLoading: boolean;
  error: unknown;
}

export interface ActivationChecks {
  /** Either read is still in flight — the summary says "checking", not a count. */
  loading: boolean;
  rows: ActivationCheck[];
  passed: number;
  attention: number;
  unverified: number;
  notes: number;
}

/** The device-code probe in the minting-account panel's own words (FD-592). */
function deviceCodeRow(
  readiness: MintReadiness | null | undefined,
  method: SignInMethod,
): ActivationCheck | null {
  const state = deviceCodeBlockState(readiness);
  const label = 'Device-code sign-in';
  if (state.kind === 'not-asked') return null; // silence about an unasked question
  if (state.kind === 'unknown') return { id: 'device-code', label, status: 'unverified', value: 'could not check' };
  if (state.kind === 'clear') return { id: 'device-code', label, status: 'pass', value: 'no blocking policy found' };

  const names = (ps: { displayName: string }[]) => ps.map((p) => p.displayName).join(', ');
  let value: string;
  if (state.covers.length > 0) value = `blocked by ${names(state.covers)}`;
  else if (state.mayCover.length > 0) value = `may be blocked by ${names(state.mayCover)}`;
  else return { id: 'device-code', label, status: 'pass', value: `report-only: ${names(state.reportOnly)}` };

  // FD-ADR-047 §2 — a restriction on device code is a PROBLEM only for an operator
  // about to use device code. With the browser sign-in or a pasted token it is a
  // fact about a path they are not taking, so it is a quiet note, never a blocker.
  return method === 'device-code'
    ? { id: 'device-code', label, status: 'attention', value }
    : { id: 'device-code', label, status: 'note', value, hint: 'affects device code only' };
}

function licenceRow(balance: QueryState<LicenseBalance>, reserveCount: number | null): ActivationCheck {
  const label = 'Licence capacity';
  if (balance.isLoading) return { id: 'licence', label, status: 'unverified', value: 'checking…' };
  if (balance.error != null) {
    if (isLicensingNotConfigured(balance.error)) {
      return {
        id: 'licence',
        label,
        status: 'note',
        value: 'licensing is not configured for this deployment — nothing is reserved',
      };
    }
    return { id: 'licence', label, status: 'unverified', value: 'the balance could not be read' };
  }
  const data = balance.data;
  if (!data || (!data.consumable && !data.pool)) {
    return { id: 'licence', label, status: 'note', value: 'no credit balance or pool reported' };
  }

  // `typeof`, never truthiness: a balance of exactly 0 is a real answer (FD-435).
  const credits = typeof data.consumable?.available === 'number' ? data.consumable.available : null;
  const poolFree = typeof data.pool?.available === 'number' ? data.pool.available : null;
  const available =
    credits !== null ? `${credits} available` : poolFree !== null ? `${poolFree} pool slots free` : 'not reported';

  if (reserveCount === null) {
    // No server count to compare against, so no verdict — and no invented number.
    return { id: 'licence', label, status: 'unverified', value: `${available} (one reserved per device)` };
  }
  const value = `${available} (${reserveCount} will be reserved)`;
  const creditShort = credits !== null && reserveCount > credits;
  const poolShort = poolFree !== null && reserveCount > poolFree;
  // The pool is the bucket that blocks server-side, and only when credits cannot
  // cover either; a credit shortfall alone is advisory but still worth a look.
  if (creditShort || (poolShort && credits === null)) {
    return { id: 'licence', label, status: 'attention', value };
  }
  return { id: 'licence', label, status: 'pass', value };
}

export function deriveActivationChecks({
  readiness,
  balance,
  reserveCount,
  method,
}: {
  readiness: QueryState<MintReadiness>;
  balance: QueryState<LicenseBalance>;
  reserveCount: number | null;
  method: SignInMethod;
}): ActivationChecks {
  const rows: ActivationCheck[] = [];
  const data = readiness.data;

  if (readiness.isLoading) {
    // Nothing to say about the account yet; the summary reads "checking".
  } else if (readiness.error != null || !data) {
    rows.push({ id: 'account', label: 'Minting account', status: 'unverified', value: 'could not check the minting account' });
  } else if (data.serviceAccountSource === 'none' || !data.serviceAccountUpn) {
    rows.push({ id: 'account', label: 'Minting account', status: 'attention', value: 'not configured' });
  } else {
    rows.push({ id: 'account', label: 'Minting account', status: 'pass', value: data.serviceAccountUpn });
    const account = data.checked ? data.account : null;
    if (account) {
      rows.push({
        id: 'exists',
        label: 'Account exists',
        status: account.present ? 'pass' : 'attention',
        value: account.present ? 'yes' : 'not found',
      });
      rows.push({
        id: 'role',
        label: 'Directory role',
        status: account.role ? 'pass' : 'attention',
        value: account.role ?? 'none — the mint will fail with AADSTS240005',
      });
      // FD-609: three states. `null`/`undefined` is "could not verify" — unverified,
      // never a problem the probe did not find and never a pass it could not prove.
      const ca = account.caExclusionHealthy;
      rows.push({
        id: 'conditional-access',
        label: 'Conditional Access',
        status: ca === true ? 'pass' : ca === false ? 'attention' : 'unverified',
        value:
          ca === true
            ? 'no blocking policy seen'
            : ca === false
              ? 'a policy may block the sign-in'
              : 'could not verify the Conditional Access exclusion',
      });
    } else {
      rows.push({
        id: 'account-checks',
        label: 'Account checks',
        status: 'unverified',
        value: data.checkReason ?? 'The account could not be checked.',
      });
    }
    const dc = deviceCodeRow(data, method);
    if (dc) rows.push(dc);
  }

  rows.push(licenceRow(balance, reserveCount));

  const count = (s: ActivationCheckStatus) => rows.filter((r) => r.status === s).length;
  return {
    loading: readiness.isLoading || balance.isLoading,
    rows,
    passed: count('pass'),
    attention: count('attention'),
    unverified: count('unverified'),
    notes: count('note'),
  };
}
